import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaJsonLd from "@/components/base/SchemaJsonLd";
import { getArticleById, getCategoryName, getRelatedArticles } from "@/utils/news";
import ArticleBody from "@/pages/article/components/ArticleBody";
import ArticleSidebar from "@/pages/article/components/ArticleSidebar";
import RelatedArticles from "@/pages/article/components/RelatedArticles";

export default function ArticlePage() {
  const { id } = useParams();
  const article = id ? getArticleById(id) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-24 text-center">
          <div>
            <i className="ri-file-unknow-line text-5xl text-foreground-300" />
            <h1 className="mt-5 font-serif-cn text-2xl font-bold text-foreground-950">
              这篇内容可能已经下线
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              链接可能已过期，或该条快讯已被合并到新的进展中。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
              >
                回到首页
              </Link>
              <Link
                to="/category/kuaixun"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition hover:border-primary-400 hover:text-primary-600"
              >
                浏览即时快讯
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "NewsArticle",
        headline: article.title,
        description: article.summary,
        datePublished: article.publishedAt,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "51cg网" },
        articleSection: getCategoryName(article.category),
        keywords: article.tags.join(","),
        inLanguage: "zh-CN",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ryvucy.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: getCategoryName(article.category),
            item: `https://ryvucy.cn/category/${article.category}`,
          },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Header />
      <main className="flex-1">
        <SchemaJsonLd data={schema} />
        <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
          <Breadcrumb
            items={[
              { label: getCategoryName(article.category), to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="mt-6 grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
            <div className="min-w-0">
              <ArticleBody article={article} />
              <RelatedArticles list={related} />
            </div>
            <div className="lg:sticky lg:top-36">
              <ArticleSidebar />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}